import React, { useContext, useEffect, useMemo, useState } from 'react';
import TileButton from 'components/atoms/TileButton';
import PromoLibraryContainer from 'components/molecules/TradePromoOptimization/PromoLibraryContainer';
import MechanicsLibraryContainer from 'components/molecules/TradePromoOptimization/MechanicsLibraryContainer';
import ProductConstraintsContainer from 'components/molecules/TradePromoOptimization/ProductConstraintsContainer';
import CustomProductGroupContainer from 'components/molecules/TradePromoOptimization/CustomProductGroup/CustomProductGroupContainer';
import { tradePromoOptimizationUrls } from 'config/urls';
import { useCommonApiHandler } from 'hooks/general/useCommonApiHandler';
import { TPODataContext } from 'contexts/TPOContext';
import { queryClient } from 'services/reactQueryService';
import { trackEventMixpanel } from 'utils/mixpanel';
import { TPO_TOOLS_SUB_TAB_CHANGED } from 'utils/mixpanelEventNames';
import { UserContext } from 'contexts/ConfigContext';
import { getConfig } from 'utils/helperMethods';

export enum ToolTabKeys {
    PromoLibrary = 'promo_library',
    PromoLibraryView = 'promo_library_view',
    MerchCorePromos = 'pl_merch_core_promos',
    MerchFDOverlays = 'pl_merch_f_and_d_overlays',
    MDF = 'pl_mdf',
    MechanicsLibrary = 'mechanic_library',
    ProductConstraints = 'product_constraint',
    Tactic = 'pl_tactic',
    TacticOffer = 'pl_tactic_offer',
    FDQuality = 'pl_f_and_d_quality',
    CustomProductGroup = 'custom_product_group',
}

type ToolTab = {
    key: string;
    label: string;
};

const subHeader = '';
const Tools = () => {
    const {
        setUiDimensionConfigData,
        setFilterUIDimensionsLoading,
        updateFilterObjectWithPlansSharing,
        uiDimensionConfigData,
    } = useContext(TPODataContext);
    const { appConfig } = useContext(UserContext);
    const { toolTabs } = getConfig('tpo', appConfig);

    const toolsQueryKeys = {
        promoLibrary: 'tpo-pl-promo-library',
        tactic: 'tpo-tactic',
        tacticOffer: 'tpo-tactic-offer',
        fdQuality: 'tpo-f-and-d-quality',
        productConstraints: 'tpo-product-constraints',
    };

    const subTileData = [
        {
            label: 'Tactic Constraints',
            key: ToolTabKeys.Tactic,
            component: (
                <PromoLibraryContainer
                    libraryGroup={ToolTabKeys.Tactic}
                    resetLabel="Tactic Constraints"
                    tableId="tactic-table-id"
                    queryKeyPrefix={toolsQueryKeys.tactic}
                    kpiConfigUrlSuffix="pl_tactic"
                    attributeTypesConfigKey="mechanicsLibraryAttributeTypes"
                    exportFileName="pl_tactic.csv"
                    tableDataUrl={tradePromoOptimizationUrls.getPromoLibraryTableDataUrl}
                />
            ),
        },
        {
            label: 'Offer Constraints',
            key: ToolTabKeys.TacticOffer,
            component: (
                <PromoLibraryContainer
                    libraryGroup={ToolTabKeys.TacticOffer}
                    resetLabel="Offer Constraints"
                    tableId="tactic-offer-table-id"
                    queryKeyPrefix={toolsQueryKeys.tacticOffer}
                    kpiConfigUrlSuffix="pl_tactic_offer"
                    attributeTypesConfigKey="mechanicsLibraryAttributeTypes"
                    exportFileName="pl_tactic_offer.csv"
                    tableDataUrl={tradePromoOptimizationUrls.getPromoLibraryTableDataUrl}
                />
            ),
        },
        {
            label: 'F & D Constraints',
            key: ToolTabKeys.FDQuality,
            component: (
                <PromoLibraryContainer
                    libraryGroup={ToolTabKeys.FDQuality}
                    resetLabel="F & D Constraints"
                    tableId="fd-quality-table-id"
                    queryKeyPrefix={toolsQueryKeys.fdQuality}
                    kpiConfigUrlSuffix="pl_f_and_d_quality"
                    attributeTypesConfigKey="mechanicsLibraryAttributeTypes"
                    exportFileName="pl_f_and_d_quality.csv"
                    tableDataUrl={tradePromoOptimizationUrls.getPromoLibraryTableDataUrl}
                />
            ),
        },
        {
            label: 'Product Constraints',
            key: ToolTabKeys.ProductConstraints,
            component: (
                <PromoLibraryContainer
                    libraryGroup={ToolTabKeys.ProductConstraints}
                    resetLabel="Product Constraints"
                    tableId="product-constraint-table-id"
                    queryKeyPrefix={toolsQueryKeys.productConstraints}
                    kpiConfigUrlSuffix="product_constraint"
                    attributeTypesConfigKey="mechanicsLibraryAttributeTypes"
                    exportFileName="product_constraint.csv"
                    tableDataUrl={tradePromoOptimizationUrls.getPromoLibraryTableDataUrl}
                />
            ),
        },
        // {
        //     label: 'Product Constraints',
        //     key: ToolTabKeys.ProductConstraints,
        //     component: <ProductConstraintsContainer resetLabel="Product Constraints" />,
        // },
        {
            label: 'Promo Library',
            key: ToolTabKeys.PromoLibraryView,
            component: (
                <PromoLibraryContainer
                    libraryGroup={ToolTabKeys.PromoLibraryView}
                    resetLabel="Promo Library"
                    tableId="promo-library-table-id"
                    queryKeyPrefix={toolsQueryKeys.promoLibrary}
                    kpiConfigUrlSuffix="promo_library_view"
                    attributeTypesConfigKey="mechanicsLibraryAttributeTypes"
                    exportFileName="promo_library.csv"
                    tableDataUrl={tradePromoOptimizationUrls.getPromoLibraryTableDataUrl}
                />
            ),
        },
        {
            label: 'Custom Product Group',
            key: ToolTabKeys.CustomProductGroup,
            component: <CustomProductGroupContainer />,
        },
        // {
        //     label: 'Mechanics library',
        //     key: ToolTabKeys.MechanicsLibrary,
        //     component: <MechanicsLibraryContainer resetLabel="Mechanics library" />,
        // },

        // { label: 'Playbook', key: 'playbook', component: <PlaybookContainer /> },
    ];

    const filteredTabs = useMemo(() => {
        if (!toolTabs) return [];

        return subTileData.filter((tab) => toolTabs.some((t: ToolTab) => t.key === tab.key));
    }, [toolTabs, subTileData]);

    const [selectedTile, setSelectedTile] = useState(filteredTabs[0]);
    const { loading: filterUILoading } = useCommonApiHandler({
        method: 'get',
        url: `${tradePromoOptimizationUrls.getPromoLibraryUiDimensions}/${selectedTile.key}`,
        queryKey: [
            'get-promo-library-ui-dims',
            `get-promo-library-ui-dims-${selectedTile.key}`,
            { selectedTile: selectedTile.key },
        ],
        queryConfig: { enabled: !!selectedTile.key && selectedTile.key !== ToolTabKeys.CustomProductGroup },
        parserFunction: (res: any) => {
            if (res && Array.isArray(res)) {
                // Filter and map for dim1 with is_dim true
                const dim1DropdownList = res
                    .filter((item) => item.dimension === 'dim1' && item.is_dim)
                    .map((item) => ({ label: item.label, value: item.value, dimension: item.dimension }));

                const defaultDim1DropdownItem = dim1DropdownList[dim1DropdownList?.length - 1];

                // Filter and map for dim2 with is_dim true
                const dim2DropdownList = res
                    .filter((item) => item.dimension === 'dim2' && item.is_dim)
                    .map((item) => ({ label: item.label, value: item.value, dimension: item.dimension }));

                const defaultDim2DropdownItem = dim2DropdownList[dim2DropdownList?.length - 1];

                // Filter and map for scenarioFilterKeys with is_filter true
                const filterKeys = res
                    .filter((item) => item.is_filter)
                    .map((item) => ({ label: item.label, value: item.value, dimension: item.dimension }));
                //DIM1 Attribute type info
                const dim1Attribute = res.find((item) => {
                    return item.is_project_dim && !item?.nested_id && item.dimension === 'dim1';
                });
                //DIM1 nested Attribute type info

                //DIM2 Attribute type info

                const dim2Attribute = res.find((item) => {
                    return item.dimension === 'dim2';
                });
                //lowest dim names for lenses
                const lowestDimLensNames = res.filter((item) => item.is_lowest).map((item) => item.value);
                //for multi selected dropdown header
                const filterDimensionInfo = res
                    .filter((item) => item.is_lowest)
                    .reduce((acc, item) => {
                        acc[item.dimension] = { label: item.label, key: item.value };
                        return acc;
                    }, {});
                //updating all filter objects to persist filter selections with Plans sharing
                updateFilterObjectWithPlansSharing(filterKeys);
                //setting all required states
                setUiDimensionConfigData({
                    defaultDim1DropdownItem,
                    defaultDim2DropdownItem,
                    dim1DropdownList,
                    dim2DropdownList,
                    filterKeys,
                    dim1Attribute: dim1Attribute
                        ? {
                              dimension: dim1Attribute.dimension,
                              key: dim1Attribute.value,
                              label: dim1Attribute.label,
                          }
                        : null,
                    dim2Attribute: dim2Attribute
                        ? {
                              dimension: dim2Attribute.dimension,
                              key: dim2Attribute.value,
                              label: dim2Attribute.label,
                          }
                        : null,

                    lowestDimLensNames,
                    filterDimensionInfo,
                });
            }

            return res;
        },
    });

    useEffect(() => {
        setFilterUIDimensionsLoading(filterUILoading);
    }, [filterUILoading]);

    //remove ui dimensions and table data for tabs from cache when route changes
    useEffect(() => {
        return () => {
            Object.values(toolsQueryKeys).forEach((queryKey) => {
                queryClient.removeQueries({
                    queryKey: [`${queryKey}-kpi-config`],
                    exact: false,
                });
            });
            queryClient.removeQueries({
                queryKey: ['tpo-promo-library-data'],
                exact: false,
            });
            queryClient.removeQueries({
                queryKey: ['get-promo-library-ui-dims'],
                exact: false,
            });
            setUiDimensionConfigData({ ...uiDimensionConfigData, filterKeys: [] });
        };
    }, []);

    useEffect(() => {
        if (selectedTile?.key) {
            trackEventMixpanel(TPO_TOOLS_SUB_TAB_CHANGED, {
                tabName: selectedTile?.label,
            });
        }
    }, [selectedTile?.key]);
    return (
        <div className="relative">
            <h5 className={`font-xs m-0 font-normal text-fontGreyAlt mt-2`}>{subHeader} </h5>
            <div className="my-2 border-b border-borderGreyAlt"></div>
            <div className="absolute inset-0 top-3">
                <TileButton
                    style={{ width: 'min-content' }}
                    data={filteredTabs}
                    activeKey={selectedTile.key}
                    onClick={(item: any) => {
                        if (item?.key === ToolTabKeys.PromoLibraryView) {
                            queryClient.removeQueries({
                                queryKey: ['tpo-promo-library-data', `${toolsQueryKeys.promoLibrary}-table-data`],
                                exact: false,
                            });
                        }
                        if (item?.key === selectedTile?.key) return;
                        //resetting filterkeys on tab change
                        setUiDimensionConfigData({ ...uiDimensionConfigData, filterKeys: [] });
                        setSelectedTile(item);
                    }}
                />
            </div>
            {selectedTile?.component}
        </div>
    );
};

export default Tools;
